import { QueryClient, QueryClientProvider, type Query } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState, type ReactNode } from 'react';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';

export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60_000,
            gcTime: 10 * 60_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              if (error instanceof JsonApiHttpError) {
                if (error.status === 401 || error.status === 403 || error.status === 404) return false;
              }
              return failureCount < 2;
            },
          },
          mutations: { retry: 0 },
        },
      })
  );
  /*
   * Finché `/me` non risponde ogni modulo vale acceso, e un widget può partire e prendere il 403
   * MODULE_DISABLED. Quando poi il modulo risulta spento la query si disattiva, ma l'errore
   * resterebbe lì: la si riporta allo stato iniziale.
   */
  useEffect(() => client.getQueryCache().subscribe((event) => {
    if (event.type !== 'observerOptionsUpdated' && event.type !== 'updated') return;
    const query = event.query;
    if (query.state.status !== 'error' || !isModuleDisabled(query.state.error)) return;
    if (query.observers.length === 0 || !query.observers.every((o) => !isEnabled(o.options.enabled, query))) return;
    void client.resetQueries({ queryKey: query.queryKey, exact: true });
  }), [client]);

  return (
    <QueryClientProvider client={client}>
      {children}
      {import.meta.env.DEV && <ReactQueryDevtools buttonPosition="bottom-left" />}
    </QueryClientProvider>
  );
}

function isModuleDisabled(error: unknown): boolean {
  return error instanceof JsonApiHttpError && error.errors.some((e) => e.code === 'MODULE_DISABLED');
}

function isEnabled(enabled: unknown, query: Query): boolean {
  if (typeof enabled === 'function') return Boolean((enabled as (q: Query) => unknown)(query));
  return enabled !== false;
}
